/* ASK A VET(TM) Constipation / Megacolon / Colonoscopy Prep Calculator
   Engine + UI. Pure calculation functions are exposed on AAVCALC for node testing. */
'use strict';

var AAV_DATA = {
  version: '1.0',
  maropitant: { mgkg: 1, route: 'IV', freq: 'q24h', vialMgMl: 10 },
  dogprep: { mlkg: 60, doses: 2, gapH: 2, offMinH: 8, offMaxH: 12, fastMinH: 24, fastMaxH: 36 },
  catprep: { mlkg: 30, doses: 2, gapH: 2, offMinH: 8, offMaxH: 12, fastMinH: 24, fastMaxH: 36 },
  catdeob: { rateMin: 6, rateMax: 10, typHrs: 8, minHrs: 5, maxHrs: 24 },
  catlax:  { tspMin: 0.125, tspMax: 0.25, gMin: 0.7, gMax: 1.4, freq: 'BID' },
  enema:   { mlkgMin: 5, mlkgMax: 10 },  /* supplementary, beyond the written protocol */
  recipe:  { pegPerBottleG: 73, bottleMl: 360, batchBottles: 3, batchPegG: 220, batchMl: 1080,
             conc: { pegGL: 210, naGL: 3.09, clGL: 3.72, kGL: 0.234 } },
  lb2kg: 0.45359237,
  /* expected text of every integrity-checked static table cell (normalised whitespace) */
  integrity: {
    maro:  'Maropitant 1 mg/kg IV q24h',
    maro2: 'Maropitant 1 mg/kg IV q24h',
    maro3: 'Maropitant 1 mg/kg IV q24h',
    deobrate: 'NGT at 6–10 mL/kg/hr for 8 hours (published range 5–24 hours)',
    catprep: '30 mL/kg via NGT 8–12 hours before the procedure, repeated 2 hours after the first dose',
    dogprep: '60 mL/kg via NGT 8–12 hours before the procedure, repeated 2 hours after the first dose',
    fastcat: 'Fast 24–36 hours',
    fastdog: 'NPO 24–36 hours (NGT liquid nutrition or elemental diet may be used)',
    enemacat: 'Warm water enema after each dose; third enema just before anaesthesia',
    enemadog: 'Warm water enema after each dose; third enema just before anaesthesia',
    lax: 'Plain PEG 3350 powder (Miralax, no electrolytes): start 1/8–1/4 level teaspoon in food twice daily, titrated upwards as needed',
    'rx-peg': '220 g total, divided into 3 portions of about 73 g',
    'rx-ped': '3 × 360 mL bottles',
    'rx-method': 'Add ~73 g Miralax to each 360 mL bottle and dissolve',
    'rx-yield': 'Approximately 1 litre (1080 mL) from 3 bottles',
    'c-peg': '210 g/L', 'c-na': '3.09 g/L', 'c-cl': '3.72 g/L', 'c-k': '0.234 g/L'
  }
};

var PROTO_META = {
  catdeob: { name: 'Cat · medical deobstipation', species: 'cat', lavage: true,  timeLbl: 'Planned infusion start' },
  catprep: { name: 'Cat · colonoscopy prep',      species: 'cat', lavage: true,  timeLbl: 'Planned procedure time' },
  dogprep: { name: 'Dog · colonoscopy prep',      species: 'dog', lavage: true,  timeLbl: 'Planned procedure time' },
  catlax:  { name: 'Cat · maintenance laxative',  species: 'cat', lavage: false, timeLbl: '' }
};

/* ---------- formatting ---------- */
function fmt(n) {
  if (!isFinite(n)) return '–';
  if (n === 0) return '0';
  var neg = n < 0; var a = Math.abs(n);
  var out;
  if (a < 0.0001) out = a.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  else if (Number(a.toPrecision(4)) >= 1000) out = Math.round(a).toLocaleString('en-AU');
  else out = String(Number(a.toPrecision(4)));
  if (out.indexOf('e') !== -1) out = a.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  return (neg ? '-' : '') + out;
}
function pad2(x) { return (x < 10 ? '0' : '') + x; }
var DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
var MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtTime(d, withYear) {
  var showYear = withYear || d.getFullYear() !== new Date().getFullYear();
  return DAYS[d.getDay()] + ' ' + d.getDate() + ' ' + MONTHS[d.getMonth()] + (showYear ? ' ' + d.getFullYear() : '') + ', ' + pad2(d.getHours()) + ':' + pad2(d.getMinutes());
}
function addHours(d, h) { return new Date(d.getTime() + h * 3600000); }

/* ---------- input parsing ---------- */
function parseWeight(raw, unit) {
  var s = String(raw == null ? '' : raw).trim();
  if (s === '') return { ok: false, err: 'Enter the body weight to build the plan.' };
  if (/,\d{3}(\.|$)/.test(s)) return { ok: false, err: 'Ambiguous number – a comma followed by three digits reads as a thousands separator. Use a decimal point (e.g. 4.5).' };
  s = s.replace(',', '.');
  if (!/^(\d+\.?\d*|\.\d+)$/.test(s)) return { ok: false, err: 'Weight must be a number (e.g. 4.5).' };
  var v = parseFloat(s);
  if (!(v > 0)) return { ok: false, err: 'Weight must be greater than zero.' };
  var kg = unit === 'lb' ? v * AAV_DATA.lb2kg : v;
  if (kg > 200) return { ok: false, err: 'That weight is implausible – check the number and units.' };
  return { ok: true, kg: kg, entered: v, unit: unit };
}
function parseWhen(str) {
  if (!str) return null;
  var d = new Date(str);
  if (isNaN(d.getTime())) return null;
  return d;
}

/* ---------- prep (solution recipe scaling) ---------- */
function scaleRecipe(needMl) {
  var R = AAV_DATA.recipe;
  var bottles = Math.max(1, Math.ceil(needMl / R.bottleMl));
  return {
    needMl: needMl,
    bottles: bottles,
    miralaxG: bottles * R.pegPerBottleG,
    pedialyteMl: bottles * R.bottleMl,
    madeMl: bottles * R.bottleMl,
    spareMl: bottles * R.bottleMl - needMl
  };
}

/* ---------- warning helpers ---------- */
var SEV_RANK = { red: 0, amber: 1, info: 2, ok: 3 };
function sortWarnings(ws) {
  return ws.slice().sort(function (a, b) {
    var ra = SEV_RANK[a.lvl], rb = SEV_RANK[b.lvl];
    if (ra === undefined) ra = 2; if (rb === undefined) rb = 2;
    if (ra !== rb) return ra - rb;
    return (a.constant ? 1 : 0) - (b.constant ? 1 : 0);
  });
}

/* ---------- the engine ---------- */
function computePlan(state) {
  var D = AAV_DATA, meta = PROTO_META[state.proto];
  var plan = { proto: state.proto, meta: meta, blocked: false, err: '', cards: [], schedule: [], warnings: [], prep: null, headline: {}, anchor: null };
  var w = null;

  if (state.proto !== 'catlax') {
    w = parseWeight(state.weightRaw, state.unit);
    if (!w.ok) { plan.blocked = true; plan.err = w.err; }
  }
  var kgDisp = null, kgN = null, mgDisp = null;
  if (w && w.ok) {
    kgDisp = fmt(w.kg); kgN = Number(kgDisp.replace(/,/g, ''));
    plan.weightKg = w.kg; plan.kgDisp = kgDisp;
    mgDisp = fmt(kgN * D.maropitant.mgkg);
    var mlVal = Math.round(Number(mgDisp.replace(/,/g, '')) / D.maropitant.vialMgMl * 100) / 100;
    plan.maropitant = { mgDisp: mgDisp, mlDisp: String(mlVal), mlVal: mlVal };
    /* weight plausibility */
    if (meta.species === 'cat' && w.kg >= 10) plan.warnings.push({ lvl: 'amber', kind: 'wt', text: 'Caution. ' + kgDisp + ' kg is heavy for a cat – double-check the weight and the units toggle.' });
    if (meta.species === 'cat' && w.kg <= 1) plan.warnings.push({ lvl: 'amber', kind: 'wt', text: 'Caution. ' + kgDisp + ' kg is very light – confirm the weight, and reassess whether this protocol fits a patient this small.' });
    if (meta.species === 'dog' && w.kg >= 100) plan.warnings.push({ lvl: 'amber', kind: 'wt', text: 'Caution. ' + kgDisp + ' kg is exceptionally heavy – double-check the weight and the units toggle.' });
    if (meta.species === 'dog' && w.kg <= 1) plan.warnings.push({ lvl: 'amber', kind: 'wt', text: 'Caution. ' + kgDisp + ' kg is very light – confirm the weight and units.' });
    if (plan.maropitant && plan.maropitant.mlVal < 0.1) plan.warnings.push({ lvl: 'amber', kind: 'wt', text: 'Caution. The maropitant volume is under 0.1 mL – dilute for accurate measurement.' });
  }

  var anchor = meta.lavage ? parseWhen(state.procatStr) : null;
  plan.anchor = anchor;

  /* ----- protocol maths ----- */
  if (state.proto === 'catdeob' && !plan.blocked) {
    var rateDisp = fmt(state.rate * kgN);
    var rateN = Number(rateDisp.replace(/,/g, ''));
    var totDisp = fmt(rateN * state.durh);
    var loDisp = fmt(D.catdeob.rateMin * kgN), hiDisp = fmt(D.catdeob.rateMax * kgN);
    plan.cards.push({
      lab: 'NGT infusion rate', num: rateDisp + ' mL/hr',
      sub: 'PEG 3350 electrolyte solution via nasogastric tube. Protocol range for this cat: ' + loDisp + '–' + hiDisp + ' mL/hr.',
      working: '<b>' + state.rate + ' mL/kg/hr × ' + kgDisp + ' kg = ' + rateDisp + ' mL/hr</b>'
    });
    plan.cards.push({
      lab: 'Total volume', num: totDisp + ' mL over ' + state.durh + ' h',
      sub: 'Typical duration 8 h; published range ' + D.catdeob.minHrs + '–' + D.catdeob.maxHrs + ' h. Stop when productive.',
      working: '<b>' + rateDisp + ' mL/hr × ' + state.durh + ' h = ' + totDisp + ' mL</b>'
    });
    plan.cards.push(maropitantCard(plan, kgDisp));
    plan.prep = scaleRecipe(Number(totDisp.replace(/,/g, '')));
    plan.headline = { main: rateDisp + ' mL/hr × ' + state.durh + ' h NGT', sub: 'Maropitant ' + mgDisp + ' mg IV · total ' + totDisp + ' mL' };
    plan.schedule = deobSchedule(state, anchor, { rateDisp: rateDisp, totDisp: totDisp, mgDisp: mgDisp });
  }

  if ((state.proto === 'dogprep' || state.proto === 'catprep') && !plan.blocked) {
    var P = D[state.proto];
    var perDisp = fmt(P.mlkg * kgN);
    var perN = Number(perDisp.replace(/,/g, ''));
    var totalDisp = fmt(perN * P.doses);
    plan.cards.push({
      lab: 'PEG per NGT dose', num: perDisp + ' mL',
      sub: 'PEG 3350 electrolyte solution via nasogastric tube, ' + P.doses + ' doses: the first ' + state.offh + ' h before the procedure, repeated ' + P.gapH + ' h later.',
      working: '<b>' + P.mlkg + ' mL/kg × ' + kgDisp + ' kg = ' + perDisp + ' mL per dose</b>'
    });
    plan.cards.push({
      lab: 'Total PEG volume', num: totalDisp + ' mL',
      sub: 'Across both doses. Follow each dose with a warm water enema; third enema just before anaesthesia.',
      working: '<b>' + perDisp + ' mL × ' + P.doses + ' doses = ' + totalDisp + ' mL</b>'
    });
    plan.cards.push(maropitantCard(plan, kgDisp));
    plan.prep = scaleRecipe(Number(totalDisp.replace(/,/g, '')));
    plan.headline = { main: perDisp + ' mL per NGT dose × ' + P.doses, sub: 'Maropitant ' + mgDisp + ' mg IV · total ' + totalDisp + ' mL' };
    plan.enemaLo = fmt(D.enema.mlkgMin * kgN); plan.enemaHi = fmt(D.enema.mlkgMax * kgN);
    plan.schedule = prepSchedule(state, anchor, { perDisp: perDisp, mgDisp: mgDisp, enemaLo: plan.enemaLo, enemaHi: plan.enemaHi });
  }

  if (state.proto === 'catlax') {
    plan.cards.push({
      lab: 'Starting laxative dose', num: '1/8 – 1/4 level teaspoon BID',
      sub: 'Plain PEG 3350 powder (Miralax, no electrolytes) mixed into food; roughly ' + D.catlax.gMin + '–' + D.catlax.gMax + ' g per dose. Titrate upwards as needed.',
      working: '<b>Fixed starting dose from the protocol – not weight-based.</b> Adjust every few days to stool quality.'
    });
    plan.headline = { main: '1/8 – 1/4 level tsp BID in food', sub: 'Plain PEG 3350 · titrate to 1–2 soft formed stools/day' };
    plan.schedule = [
      { when: 'Start', what: 'PEG 3350 powder 1/8–1/4 level tsp in food, twice daily', sub: 'plain Miralax-type powder, no electrolytes' },
      { when: 'Days 3–7', what: 'Titrate the dose', sub: 'adjust in 1/8-tsp steps every few days to effect' },
      { when: 'Target', what: '1–2 soft, formed stools per day', sub: 'reduce the dose if stools become liquid' },
      { when: 'Review', what: 'Recheck if straining, vomiting or no stool for 48–72 h', sub: 'reassess for impaction and the underlying cause' }
    ];
  }

  /* ----- warnings ----- */
  buildWarnings(state, plan);
  plan.warnings = sortWarnings(plan.warnings);
  return plan;
}

function maropitantCard(plan, kgDisp) {
  var m = plan.maropitant;
  return {
    lab: 'Maropitant (antiemetic)', num: m.mgDisp + ' mg IV q24h',
    sub: '≈ ' + m.mlDisp + ' mL of a 10 mg/mL injection. Read the strength off the vial. Give slowly IV over 1–2 minutes.',
    working: '<b>1 mg/kg × ' + kgDisp + ' kg = ' + m.mgDisp + ' mg</b>; ' + m.mgDisp + ' mg ÷ 10 mg/mL ≈ ' + m.mlDisp + ' mL'
  };
}

function schedRow(anchor, off, offLabel, what, sub) {
  var when, whenSub = '';
  if (anchor) {
    when = fmtTime(addHours(anchor, off));
    if (offLabel) whenSub = offLabel;
  } else {
    if (off === 0) when = 'T';
    else when = 'T ' + (off < 0 ? '−' : '+') + ' ' + Math.abs(off) + ' h';
    if (offLabel) whenSub = offLabel;
  }
  return { when: when, whenSub: whenSub, what: what, sub: sub };
}

function prepSchedule(state, anchor, v) {
  var rows = [];
  rows.push(schedRow(anchor, -state.fasth, '', 'Withhold food', state.proto === 'dogprep' ? 'NPO ' + state.fasth + ' h; NGT liquid nutrition or elemental diet may be used' : 'fast ' + state.fasth + ' h per protocol'));
  rows.push(schedRow(anchor, -state.offh, 'with dose 1', 'Maropitant ' + v.mgDisp + ' mg IV', '1 mg/kg q24h, before lavage begins'));
  rows.push(schedRow(anchor, -state.offh, '', 'PEG dose 1: ' + v.perDisp + ' mL via NGT', 'confirm NGT placement first; give slowly over 30–60 min, never as a bolus'));
  rows.push(schedRow(anchor, -state.offh, 'after dose 1', 'Warm water enema', 'commonly ' + v.enemaLo + '–' + v.enemaHi + ' mL warm water, given slowly (volume is beyond the written protocol)'));
  rows.push(schedRow(anchor, -state.offh + 2, '', 'PEG dose 2: ' + v.perDisp + ' mL via NGT', 'repeat 2 h after dose 1; give slowly as for dose 1'));
  rows.push(schedRow(anchor, -state.offh + 2, 'after dose 2', 'Warm water enema', 'as above'));
  rows.push(schedRow(anchor, 0, 'before anaesthesia', 'Third warm water enema', 'immediately before anaesthesia'));
  rows.push(schedRow(anchor, 0, '', 'Procedure', 'colonoscopy under anaesthesia'));
  return rows;
}

function deobSchedule(state, anchor, v) {
  var rows = [];
  rows.push({ when: 'First', whenSub: '', what: 'Rehydrate and correct electrolytes', sub: 'especially potassium – hypokalaemia itself impairs colonic motility' });
  rows.push(schedRow(anchor, 0, '', 'Maropitant ' + v.mgDisp + ' mg IV', '1 mg/kg q24h'));
  rows.push(schedRow(anchor, 0, '', 'Place and confirm NGT', 'verify position before any solution is given'));
  rows.push(schedRow(anchor, 0, '', 'Start PEG infusion at ' + v.rateDisp + ' mL/hr', state.rate + ' mL/kg/hr, PEG 3350 electrolyte solution'));
  rows.push({ when: anchor ? 'During' : 'T → T + ' + state.durh + ' h', whenSub: '', what: 'Monitor', sub: 'expect faeces within 6–12 h; watch for nausea, regurgitation, distension' });
  rows.push(schedRow(anchor, state.durh, '', 'Stop infusion', 'total ' + v.totDisp + ' mL over ' + state.durh + ' h'));
  rows.push({ when: 'Then', whenSub: '', what: 'Reassess', sub: 'palpate ± repeat imaging; the published range extends to 24 h if needed, or escalate to anaesthetised evacuation' });
  return rows;
}

function buildWarnings(state, plan) {
  var W = plan.warnings, meta = plan.meta, f = state.flags;
  var blocked = plan.blocked;
  var isPrep = state.proto === 'dogprep' || state.proto === 'catprep';
  if (f.obstr) W.push({ lvl: 'red', kind: 'flag', text: 'Critical. PEG lavage and laxatives are contraindicated with small intestinal obstruction, perforation or untreated ileus. Image and rule these out before giving anything by mouth or tube.' });
  if (meta.lavage) {
    if (!blocked) {
      W.push({ lvl: 'red', kind: 'ngt', text: 'Critical. Confirm nasogastric tube placement before every dose (radiograph, end-tidal CO₂ trace or aspirate pH). PEG solution delivered to the airway is life-threatening.' });
      if (meta.species === 'cat') W.push({ lvl: 'red', kind: 'fleet', text: 'Critical. Never use sodium phosphate (Fleet-type) enemas in cats. They cause fatal hyperphosphataemia and hypocalcaemia. Warm water only.' });
      else if (plan.weightKg && plan.weightKg < 10) W.push({ lvl: 'red', kind: 'fleet', text: 'Critical. Never use sodium phosphate (Fleet-type) enemas in small dogs. Warm water only.' });
      else W.push({ lvl: 'info', kind: 'fleet', text: 'Note. Use warm water enemas only – sodium phosphate (Fleet-type) enemas are dangerous in cats and small dogs.' });
      if (isPrep) W.push({ lvl: 'amber', kind: 'bolus', text: 'Caution. Give each NGT dose slowly – over 30–60 minutes by aliquots or infusion pump, never as a rapid bolus. These volumes approach gastric capacity.' });
      if (isPrep) W.push({ lvl: 'amber', kind: 'induction', text: 'Caution. Anaesthesia follows large-volume liquid dosing – assume a fluid-filled stomach at induction. Intubate promptly with a cuffed tube and have suction ready.' });
      W.push({ lvl: 'amber', kind: 'drug', text: 'Caution. All lavage volumes on this plan use PEG 3350 WITH electrolytes (GoLYTELY-type or the Miralax + Pedialyte recipe below). Plain Miralax on its own is only for the maintenance laxative dose.' });
    }
    if (f.dehy) W.push({ lvl: 'amber', kind: 'flag', text: 'Caution. Correct dehydration and electrolytes, especially potassium, before starting. Lavaging a dehydrated, hypokalaemic patient treats the radiograph, not the cat.' });
    if (f.vomit) W.push({ lvl: 'amber', kind: 'flag', text: 'Caution. Active vomiting, regurgitation or brachycephalic anatomy raises aspiration risk during large-volume NGT dosing. Ensure maropitant is on board, dose slowly, and keep the head up.' });
    if (f.cardio) W.push({ lvl: 'amber', kind: 'flag', text: 'Caution. Significant cardiac or renal disease: large-volume lavage still shifts fluid transiently. Use the lower end of rates and volumes and monitor closely.' });
    if (!blocked) W.push({ lvl: 'info', kind: 'maro', text: 'Note. Give maropitant slowly IV over 1–2 minutes. If dosing SC instead, a refrigerated vial reduces the injection sting – do not warm it.' });
  } else {
    W.push({ lvl: 'amber', kind: 'drug', text: 'Caution. The maintenance laxative is PLAIN PEG 3350 with no electrolytes. It is not the lavage solution and must not be used at lavage volumes.' });
    if (f.dehy) W.push({ lvl: 'amber', kind: 'flag', text: 'Caution. A dehydrated or azotaemic patient needs assessment and rehydration, not just a laxative in food.' });
    if (f.vomit) W.push({ lvl: 'amber', kind: 'flag', text: 'Caution. Vomiting alongside constipation needs a workup before starting or increasing laxatives.' });
    if (f.cardio) W.push({ lvl: 'info', kind: 'flag', text: 'Note. PEG 3350 at laxative doses causes minimal fluid shift, but keep the underlying cardiac or renal disease in the treatment plan.' });
  }
  if (state.proto === 'catdeob' && !blocked) W.push({ lvl: 'info', kind: 'course', text: 'Note. Most cats pass faeces within 6–12 hours of starting the infusion. If a full infusion is unproductive, reassess and re-image rather than simply extending.' });
  if (f.recur) W.push({ lvl: 'info', kind: 'flag', text: 'Note. Recurrent obstipation or radiographic megacolon needs a maintenance plan (PEG laxative titration, diet, cause-hunting) and, if refractory despite good management, a subtotal colectomy discussion.' });
  if (!blocked) W.push({ lvl: 'info', kind: 'const', constant: true, text: 'Note. Label the made-up solution with its contents, date and patient. Record batch, volumes and times in the clinical record. Read every drug strength off the vial, not off this page.' });
  plan.hasRed = W.some(function (x) { return x.lvl === 'red'; });
}

/* ---------- plain-text record (shared by copy / download / email / print meta) ---------- */
function buildPlanText(state, plan, opts) {
  opts = opts || {};
  var L = [];
  var meta = plan.meta;
  L.push('ASK A VET(TM) – Constipation / Megacolon / Colonoscopy Prep Calculator');
  L.push('Protocol: ' + meta.name.replace(' · ', ' - '));
  if (plan.kgDisp) L.push('Patient weight: ' + plan.kgDisp + ' kg');
  if (plan.anchor) L.push((meta.timeLbl || 'Anchor time') + ': ' + fmtTime(plan.anchor));
  L.push('');
  if (plan.blocked) {
    L.push('PLAN NOT CALCULATED: ' + plan.err);
  } else {
    L.push('DOSES');
    plan.cards.forEach(function (c) {
      L.push('- ' + c.lab + ': ' + c.num);
      L.push('  ' + stripTags(c.sub));
      if (!opts.noWorking) L.push('  Working: ' + stripTags(c.working));
    });
    L.push('');
    L.push('SCHEDULE');
    plan.schedule.forEach(function (r) {
      L.push('- [' + r.when + (r.whenSub ? ', ' + r.whenSub : '') + '] ' + r.what + (r.sub ? ' – ' + r.sub : ''));
    });
    if (plan.prep) {
      L.push('');
      L.push('MAKE THE PEG 3350 ELECTROLYTE SOLUTION');
      L.push('- Volume needed: ' + fmt(plan.prep.needMl) + ' mL');
      L.push('- Make ' + plan.prep.bottles + ' bottle(s): add ~73 g plain PEG 3350 (Miralax) to each 360 mL bottle of plain unflavoured Pedialyte');
      L.push('- Uses ' + fmt(plan.prep.miralaxG) + ' g Miralax + ' + fmt(plan.prep.pedialyteMl) + ' mL Pedialyte, makes ~' + fmt(plan.prep.madeMl) + ' mL (' + fmt(plan.prep.spareMl) + ' mL spare)');
    }
  }
  L.push('');
  L.push('CHECKS');
  plan.warnings.forEach(function (w) { L.push('- ' + stripTags(w.text)); });
  L.push('');
  L.push('Extra-label protocol for veterinary professional use. Verify all doses and strengths before administration.');
  L.push('askavet.com/pages/vet-tools');
  return L.join('\n');
}
function stripTags(s) { return String(s || '').replace(/<[^>]*>/g, ''); }

/* Email body: whole lines only, CHECKS always included. Tries full detail, then
   progressively smaller variants; red/amber checks are never dropped. */
function buildEmailBody(state, plan, limit) {
  limit = limit || 1700;
  var full = buildPlanText(state, plan);
  if (full.length <= limit) return full;
  var meta = plan.meta;
  var head = ['ASK A VET(TM) treatment plan – ' + meta.name.replace(' · ', ' - ')];
  if (plan.kgDisp) head.push('Patient weight: ' + plan.kgDisp + ' kg');
  if (plan.anchor) head.push((meta.timeLbl || 'Anchor time') + ': ' + fmtTime(plan.anchor, true));
  var doses = plan.blocked ? ['', 'PLAN NOT CALCULATED: ' + plan.err] : [''].concat(['DOSES']).concat(plan.cards.map(function (c) { return '- ' + c.lab + ': ' + c.num; }));
  var checksAll = ['', 'CHECKS'].concat(plan.warnings.map(function (w) { return '- ' + stripTags(w.text); }));
  var tail = ['', 'Full plan with working and schedule: use Print or Download on the calculator.', 'askavet.com/pages/vet-tools'];
  var sched = plan.blocked ? [] : ['', 'SCHEDULE'].concat(plan.schedule.map(function (r) { return '- [' + r.when + (r.whenSub ? ', ' + r.whenSub : '') + '] ' + r.what; }));
  var withSched = head.concat(doses).concat(sched).concat(checksAll).concat(tail).join('\n');
  if (withSched.length <= limit) return withSched;
  var noSched = head.concat(doses).concat(checksAll).concat(tail).join('\n');
  if (noSched.length <= limit) return noSched;
  var checksCore = ['', 'CHECKS'].concat(plan.warnings.filter(function (w) { return w.lvl === 'red' || w.lvl === 'amber'; }).map(function (w) { return '- ' + stripTags(w.text); }));
  return head.concat(doses).concat(checksCore).concat(tail).join('\n');
}

/* ---------- URL hash state ---------- */
function buildHash(state) {
  /* always emit every calculation-driving field, empty included */
  var f = state.flags;
  var parts = [
    'p=' + encodeURIComponent(state.proto),
    'u=' + encodeURIComponent(state.unit),
    'w=' + encodeURIComponent(state.weightRaw || ''),
    'r=' + encodeURIComponent(state.rate),
    'd=' + encodeURIComponent(state.durh),
    'o=' + encodeURIComponent(state.offh),
    'fa=' + encodeURIComponent(state.fasth),
    't=' + encodeURIComponent(state.procatStr || ''),
    'fl=' + [f.obstr ? 1 : 0, f.dehy ? 1 : 0, f.vomit ? 1 : 0, f.cardio ? 1 : 0, f.recur ? 1 : 0].join('')
  ];
  return '#' + parts.join('&');
}
function parseHash(h) {
  if (!h || h.charAt(0) !== '#') return null;
  var out = {}; var any = false;
  h.slice(1).split('&').forEach(function (kv) {
    var i = kv.indexOf('='); if (i < 0) return;
    out[kv.slice(0, i)] = decodeURIComponent(kv.slice(i + 1)); any = true;
  });
  if (!any || !out.p || !PROTO_META[out.p]) return null;
  var fl = String(out.fl || '00000');
  var t = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(out.t || '') ? out.t : '';
  return {
    proto: out.p,
    unit: out.u === 'lb' ? 'lb' : 'kg',
    weightRaw: out.w || '',
    rate: snapHalf(out.r, 6, 10, 8),
    durh: snapList(out.d, [5, 6, 8, 10, 12, 18, 24], 8),
    offh: snapList(out.o, [8, 10, 12], 10),
    fasth: snapList(out.fa, [24, 30, 36], 24),
    procatStr: t,
    flags: { obstr: fl.charAt(0) === '1', dehy: fl.charAt(1) === '1', vomit: fl.charAt(2) === '1', cardio: fl.charAt(3) === '1', recur: fl.charAt(4) === '1' }
  };
}
/* snap hash-supplied settings onto the real control grid, never between options */
function snapList(v, list, dflt) {
  var n = parseFloat(v);
  if (!isFinite(n)) return dflt;
  var best = list[0];
  for (var i = 1; i < list.length; i++) if (Math.abs(list[i] - n) < Math.abs(best - n)) best = list[i];
  return best;
}
function snapHalf(v, lo, hi, dflt) {
  var n = parseFloat(v);
  if (!isFinite(n)) return dflt;
  return Math.min(hi, Math.max(lo, Math.round(n * 2) / 2));
}

var AAVCALC = {
  DATA: AAV_DATA, META: PROTO_META,
  fmt: fmt, fmtTime: fmtTime, addHours: addHours,
  parseWeight: parseWeight, parseWhen: parseWhen,
  scaleRecipe: scaleRecipe, computePlan: computePlan,
  buildPlanText: buildPlanText, buildEmailBody: buildEmailBody,
  buildHash: buildHash, parseHash: parseHash,
  sortWarnings: sortWarnings, snapList: snapList, snapHalf: snapHalf
};
if (typeof module !== 'undefined' && module.exports) module.exports = AAVCALC;

/* ================= UI ================= */
class ConstipationCalculator extends HTMLElement {
  connectedCallback() {
  if (this.dataset.initialized === 'true') return;
  this.dataset.initialized = 'true';
  var root = this;
  var state = {
    proto: 'catdeob', unit: 'kg', weightRaw: '',
    rate: 8, durh: 8, offh: 10, fasth: 24,
    procatStr: '', flags: { obstr: false, dehy: false, vomit: false, cardio: false, recur: false }
  };
  var lastPlan = null;
  function el(id) { return root.querySelector('#' + id); }
  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

  /* restore from hash */
  var fromHash = parseHash(location.hash);
  if (fromHash) {
    state = fromHash;
    el('weight').value = state.weightRaw;
    el('rate').value = state.rate;
    el('durh').value = String(state.durh);
    el('offh').value = String(state.offh);
    el('fasth').value = String(state.fasth);
    el('procat').value = state.procatStr;
    el('fl-obstr').checked = state.flags.obstr; el('fl-dehy').checked = state.flags.dehy;
    el('fl-vomit').checked = state.flags.vomit; el('fl-cardio').checked = state.flags.cardio;
    el('fl-recur').checked = state.flags.recur;
  }

  /* segmented controls */
  function wireSeg(segId, attr, get, set) {
    var seg = el(segId);
    seg.addEventListener('click', function (e) {
      var b = e.target.closest('button'); if (!b) return;
      set(b.getAttribute(attr));
      Array.prototype.forEach.call(seg.querySelectorAll('button'), function (x) {
        x.setAttribute('aria-pressed', x === b ? 'true' : 'false');
      });
      render();
    });
    /* sync pressed state to current value */
    Array.prototype.forEach.call(seg.querySelectorAll('button'), function (x) {
      x.setAttribute('aria-pressed', x.getAttribute(attr) === get() ? 'true' : 'false');
    });
  }
  wireSeg('protoSeg', 'data-proto', function () { return state.proto; }, function (v) { state.proto = v; });
  wireSeg('unitSeg', 'data-unit', function () { return state.unit; }, function (v) { state.unit = v; });

  el('weight').addEventListener('input', function () { state.weightRaw = this.value; render(); });
  el('rate').addEventListener('input', function () { state.rate = parseFloat(this.value); render(); });
  el('durh').addEventListener('change', function () { state.durh = parseFloat(this.value); render(); });
  el('offh').addEventListener('change', function () { state.offh = parseFloat(this.value); render(); });
  el('fasth').addEventListener('change', function () { state.fasth = parseFloat(this.value); render(); });
  el('procat').addEventListener('change', function () { state.procatStr = this.value; render(); });
  el('procat').addEventListener('input', function () { state.procatStr = this.value; render(); });
  ['obstr', 'dehy', 'vomit', 'cardio', 'recur'].forEach(function (k) {
    el('fl-' + k).addEventListener('change', function () { state.flags[k] = this.checked; render(); });
  });
  el('jumpBtn').addEventListener('click', function () { el('aav-plan').scrollIntoView({ behavior: 'smooth', block: 'start' }); });
  el('pinJump').addEventListener('click', function () { el('aav-plan').scrollIntoView({ behavior: 'smooth', block: 'start' }); });

  /* mode-dependent visibility + labels */
  function syncMode() {
    var meta = PROTO_META[state.proto];
    var isDeob = state.proto === 'catdeob', isPrep = state.proto === 'dogprep' || state.proto === 'catprep', isLax = state.proto === 'catlax';
    el('setDeob').style.display = isDeob ? '' : 'none';
    el('setPrep').style.display = isPrep ? '' : 'none';
    el('setLax').style.display = isLax ? '' : 'none';
    el('stepWeight').style.display = isLax ? 'none' : '';
    el('stepTime').style.display = isLax ? 'none' : '';
    el('setExp').textContent = isDeob ? 'The protocol gives a rate range and a duration range. Pick where this patient sits.'
      : isPrep ? 'The protocol gives ranges for dose timing and fasting. Pick where this patient sits.'
      : '';
    el('procatLbl').textContent = meta.timeLbl || 'Planned time';
    el('timeExp').textContent = isDeob ? 'Set the planned infusion start and the schedule becomes real clock times you can print.'
      : 'Set the planned procedure time and the schedule becomes real clock times you can print.';
    el('wtUnitLbl').textContent = '(' + state.unit + ')';
    /* renumber visible steps */
    var n = 0;
    Array.prototype.forEach.call(root.querySelectorAll('.step'), function (s) {
      if (s.style.display === 'none') return;
      n++;
      var num = s.querySelector('.snum'); if (num) num.textContent = String(n);
    });
  }

  /* rate slider echo */
  function syncRateEcho() {
    var txt = state.rate + ' mL/kg/hr';
    var w = parseWeight(state.weightRaw, state.unit);
    if (w.ok) txt += ' = ' + fmt(state.rate * Number(fmt(w.kg).replace(/,/g, ''))) + ' mL/hr for this cat';
    el('rateEcho').textContent = txt;
  }

  function weightEcho() {
    var w = parseWeight(state.weightRaw, state.unit);
    if (!state.weightRaw) { el('wtEcho').textContent = ''; return; }
    if (!w.ok) { el('wtEcho').textContent = w.err; return; }
    el('wtEcho').textContent = state.unit === 'lb' ? (fmt(w.entered) + ' lb = ' + fmt(w.kg) + ' kg') : '';
  }

  var srTimer = null;
  function announce(text) {
    clearTimeout(srTimer);
    srTimer = setTimeout(function () { el('srlive').textContent = text; }, 800);
  }

  function render() {
    syncMode(); syncRateEcho(); weightEcho();
    var plan = computePlan(state);
    /* past-time note */
    if (plan.anchor && plan.anchor.getTime() < Date.now() - 60000 && !plan.blocked) {
      plan.warnings.push({ lvl: 'info', kind: 'time', text: 'Note. The entered time is in the past – the schedule is still shown, but check the date.' });
      plan.warnings = sortWarnings(plan.warnings);
    }
    lastPlan = plan;
    var meta = plan.meta;
    el('planTitle').textContent = meta.name.replace(' · ', ' — ');
    el('planTag').textContent = plan.kgDisp ? plan.kgDisp + ' kg' : (state.proto === 'catlax' ? 'not weight-based' : '');

    var h = '';
    if (plan.blocked) {
      h += '<div class="rerr"><b class="sev">Plan not calculated.</b> ' + esc(plan.err) + '</div>';
    } else {
      plan.cards.forEach(function (c) {
        h += '<div class="bigcard"><div class="blab">' + esc(c.lab) + '</div><div class="bignum">' + esc(c.num) + '</div><div class="bigsub">' + esc(c.sub) + '</div><div class="working">' + c.working + '</div></div>';
      });
      h += '<div class="bigcard"><div class="blab">Schedule' + (plan.anchor ? '' : ' (T = ' + (state.proto === 'catdeob' ? 'infusion start' : 'procedure time') + ')') + '</div><ul class="sched">';
      plan.schedule.forEach(function (r) {
        h += '<li><span class="when">' + esc(r.when) + (r.whenSub ? '<br><span style="font-weight:400">' + esc(r.whenSub) + '</span>' : '') + '</span><span class="what"><b>' + esc(r.what) + '</b>' + (r.sub ? '<br>' + esc(r.sub) : '') + '</span></li>';
      });
      h += '</ul></div>';
    }
    el('rbody').innerHTML = h;

    /* warnings */
    var wh = '';
    plan.warnings.forEach(function (w) {
      var sevWord = w.lvl === 'red' ? 'Critical.' : w.lvl === 'amber' ? 'Caution.' : w.lvl === 'ok' ? 'Clear.' : 'Note.';
      var body = esc(w.text).replace(/^(Critical\.|Caution\.|Note\.|Clear\.)\s*/, '');
      wh += '<div class="warn ' + w.lvl + '"><b class="sev">' + sevWord + '</b> ' + body + '</div>';
    });
    el('warnbox').innerHTML = wh;

    /* prep card */
    var pc = el('prepCard'), pb = el('prepBody');
    if (plan.prep) {
      pc.style.display = '';
      var p = plan.prep;
      pb.innerHTML = '<p style="margin:0 0 10px;color:var(--ink-soft)">This plan needs <strong>' + esc(fmt(p.needMl)) + ' mL</strong> of PEG 3350 electrolyte solution. Using the in-house recipe (73 g plain PEG 3350 per 360 mL bottle of plain Pedialyte):</p>' +
        '<div class="twrap"><table><thead><tr><th scope="col">Make</th><th scope="col">Plain PEG 3350 (Miralax)</th><th scope="col">Plain Pedialyte</th><th scope="col">Yields</th></tr></thead><tbody><tr>' +
        '<td><strong>' + p.bottles + ' bottle' + (p.bottles === 1 ? '' : 's') + '</strong></td>' +
        '<td>' + esc(fmt(p.miralaxG)) + ' g (~73 g per bottle)</td>' +
        '<td>' + esc(fmt(p.pedialyteMl)) + ' mL (360 mL per bottle)</td>' +
        '<td>~' + esc(fmt(p.madeMl)) + ' mL (' + esc(fmt(p.spareMl)) + ' mL spare)</td>' +
        '</tr></tbody></table></div>' +
        '<p class="echo" style="margin-top:8px">Or use a commercial PEG 3350 electrolyte solution (GoLYTELY-type) as directed. Approximate concentrations as stated in the written protocol: PEG 210 g/L, Na 3.09 g/L, Cl 3.72 g/L, K 0.234 g/L.</p>';
    } else if (state.proto === 'catlax') {
      pc.style.display = '';
      pb.innerHTML = '<p style="margin:0;color:var(--ink-soft)">Not needed for the maintenance laxative – plain PEG 3350 powder goes straight into food. The Miralax + Pedialyte electrolyte recipe (see the reference table below) is only for lavage and deobstipation.</p>';
    } else {
      pc.style.display = 'none';
      pb.innerHTML = '';
    }

    /* pinbar */
    var pin = el('aav-pinbar');
    pin.classList.add('on');
    if (plan.blocked) {
      pin.classList.remove('alert');
      el('pinTag').textContent = 'Plan';
      el('pinMain').textContent = 'Enter the body weight';
      el('pinSub').textContent = meta.name;
    } else {
      pin.classList.toggle('alert', !!plan.hasRed && (state.flags.obstr === true));
      el('pinTag').textContent = plan.hasRed && state.flags.obstr ? 'Check critical warnings' : meta.name;
      el('pinMain').textContent = plan.headline.main;
      el('pinSub').textContent = plan.headline.sub;
    }
    root.classList.toggle('aav-pinpad', window.matchMedia('(max-width:760px)').matches);

    announce(plan.blocked ? plan.err : (meta.name + '. ' + plan.headline.main + '. ' + plan.headline.sub + '.'));
  }

  /* action buttons (static markup, wired once) */
  function wireButtons() {
    el('btnPrint').addEventListener('click', function () { buildPrint(); window.print(); });
    el('btnCopy').addEventListener('click', function () {
      var t = buildPlanText(state, lastPlan);
      copyText(t, this);
    });
    el('btnDl').addEventListener('click', function () {
      var t = buildPlanText(state, lastPlan);
      /* In the claude.ai artifact viewer, downloads go through the granted
         capability; everywhere else (askavet.com) the plain blob link works. */
      if (typeof window !== 'undefined' && window.claude && typeof window.claude.use === 'function') {
        window.claude.use('downloads').then(function (dl) {
          if (dl) dl.save({ filename: 'askavet-constipation-plan.txt', data: t }).catch(function () {});
          else blobDownload(t);
        }, function () { blobDownload(t); });
      } else blobDownload(t);
    });
    el('btnEmail').addEventListener('click', function () {
      var subj = 'ASK A VET treatment plan: ' + lastPlan.meta.name.replace(' · ', ' - ');
      var body = buildEmailBody(state, lastPlan, 1700); /* whole lines only; CHECKS always included */
      location.href = 'mailto:?subject=' + encodeURIComponent(subj) + '&body=' + encodeURIComponent(body);
    });
    el('btnShare').addEventListener('click', function () {
      var url = location.origin + location.pathname + location.search + buildHash(state);
      var self = this;
      if (navigator.share) {
        navigator.share({ title: 'ASK A VET treatment plan', url: url }).catch(function () {});
      } else {
        copyText(url, self, 'Link copied');
      }
    });
  }
  function copyText(t, btn, doneLbl) {
    var done = function () {
      var old = btn.textContent; btn.textContent = doneLbl || 'Copied';
      setTimeout(function () { btn.textContent = old; }, 1400);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(t).then(done, function () { fallbackCopy(t); done(); });
    else { fallbackCopy(t); done(); }
  }
  function blobDownload(t) {
    var blob = new Blob([t], { type: 'text/plain' });
    var a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'askavet-constipation-plan.txt';
    document.body.appendChild(a); a.click();
    setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 500);
  }
  function fallbackCopy(t) {
    var ta = document.createElement('textarea');
    ta.value = t; ta.style.position = 'fixed'; ta.style.left = '-9999px';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); } catch (e) {}
    ta.remove();
  }

  /* print sheet (single source: built from lastPlan on every print) */
  function buildPrint() {
    var plan = lastPlan || computePlan(state);
    var ps = root.querySelector('#aav-printsheet');
    var h = '<h2>ASK A VET™ – ' + esc(plan.meta.name.replace(' · ', ' — ')) + '</h2>';
    h += '<div class="pmeta">Constipation / Megacolon / Colonoscopy Prep Calculator · askavet.com/pages/vet-tools · By Dr Duncan Houston · Printed record – verify before administration</div>';
    h += '<div class="pmeta">Patient: ______________________ · Weight: ' + (plan.kgDisp ? esc(plan.kgDisp) + ' kg' : '__________') + (plan.anchor && plan.meta.timeLbl ? ' · ' + esc(plan.meta.timeLbl) + ': ' + esc(fmtTime(plan.anchor, true)) : '') + '</div>';
    if (plan.blocked) {
      h += '<div class="pwarn"><strong>Plan not calculated:</strong> ' + esc(plan.err) + '</div>';
    } else {
      h += '<h3>Doses</h3><table><thead><tr><th>Item</th><th>Dose</th><th>Notes / working</th></tr></thead><tbody>';
      plan.cards.forEach(function (c) {
        h += '<tr><td>' + esc(c.lab) + '</td><td><strong>' + esc(c.num) + '</strong></td><td>' + esc(stripTags(c.sub)) + '<br><em>' + esc(stripTags(c.working)) + '</em></td></tr>';
      });
      h += '</tbody></table>';
      h += '<h3>Schedule</h3><table><thead><tr><th style="width:24px"></th><th>When</th><th>Action</th><th>Detail</th></tr></thead><tbody>';
      plan.schedule.forEach(function (r) {
        h += '<tr><td><span class="chkbox"></span></td><td>' + esc(r.when) + (r.whenSub ? ' (' + esc(r.whenSub) + ')' : '') + '</td><td><strong>' + esc(r.what) + '</strong></td><td>' + esc(r.sub || '') + '</td></tr>';
      });
      h += '</tbody></table>';
      if (plan.prep) {
        h += '<h3>Make the PEG 3350 electrolyte solution</h3>';
        h += '<p>Needs <strong>' + esc(fmt(plan.prep.needMl)) + ' mL</strong>. Make <strong>' + plan.prep.bottles + ' bottle' + (plan.prep.bottles === 1 ? '' : 's') + '</strong>: ~73 g plain PEG 3350 (Miralax) into each 360 mL bottle of plain unflavoured Pedialyte. Total ' + esc(fmt(plan.prep.miralaxG)) + ' g Miralax + ' + esc(fmt(plan.prep.pedialyteMl)) + ' mL Pedialyte = ~' + esc(fmt(plan.prep.madeMl)) + ' mL (' + esc(fmt(plan.prep.spareMl)) + ' mL spare). Label with contents, date and patient.</p>';
      }
    }
    h += '<h3>Checks</h3>';
    plan.warnings.forEach(function (w) {
      h += '<div class="pwarn ' + (w.lvl === 'red' ? '' : w.lvl) + '">' + esc(w.text) + '</div>';
    });
    h += '<div class="psig"><div>Prepared by (name / signature / time)</div><div>Checked by (name / signature / time)</div></div>';
    h += '<div class="pmeta" style="margin-top:10px">Extra-label protocol for veterinary professional use. This sheet restates a written clinical protocol; verify all doses and strengths against product labels before administration.</div>';
    ps.innerHTML = h;
  }
  window.addEventListener('beforeprint', buildPrint);
  if (window.matchMedia) {
    var mq = window.matchMedia('print');
    if (mq && mq.addEventListener) mq.addEventListener('change', function (e) { if (e.matches) buildPrint(); });
  }

  /* integrity check: static tables + FAQ schema vs live data */
  function integrityCheck() {
    var bad = [];
    var cells = root.querySelectorAll('[data-ik]');
    Array.prototype.forEach.call(cells, function (c) {
      var k = c.getAttribute('data-ik');
      var expect = AAV_DATA.integrity[k];
      var got = c.textContent.replace(/\s+/g, ' ').trim();
      if (expect === undefined) { bad.push(k + ': no expectation defined'); return; }
      if (got !== expect.replace(/\s+/g, ' ').trim()) bad.push(k + ': "' + got + '" != "' + expect + '"');
    });
    Object.keys(AAV_DATA.integrity).forEach(function (k) {
      if (!root.querySelector('[data-ik="' + k + '"]')) bad.push(k + ': static cell missing');
    });
    /* FAQ drift: visible FAQs vs FAQPage JSON-LD */
    try {
      var scripts = root.querySelectorAll('script[type="application/ld+json"]');
      var faqNames = null;
      Array.prototype.forEach.call(scripts, function (s) {
        var j = JSON.parse(s.textContent);
        var g = j['@graph'] || [j];
        g.forEach(function (node) {
          if (node['@type'] === 'FAQPage') faqNames = node.mainEntity.map(function (q) { return q.name.replace(/\s+/g, ' ').trim(); });
        });
      });
      var vis = Array.prototype.map.call(root.querySelectorAll('#ref-faq ~ details.refsec > summary, .seo details.refsec > summary'), function (s) { return s.textContent.replace(/\s+/g, ' ').trim(); });
      if (faqNames) {
        faqNames.forEach(function (n) {
          if (vis.indexOf(n) === -1) bad.push('FAQ in schema not visible on page: ' + n);
        });
      } else bad.push('FAQPage schema missing');
    } catch (e) { bad.push('FAQ check failed: ' + e.message); }
    if (bad.length) {
      var box = root.querySelector('#integrity');
      box.classList.add('bad');
      if (window.console && console.error) console.error('AAV integrity check failed:', bad);
    }
    return bad;
  }

  /* TOC anchor: open target details */
  root.addEventListener('click', function (e) {
    var a = e.target.closest('a[href^="#"]'); if (!a) return;
    var id = a.getAttribute('href').slice(1);
    var t = root.querySelector('#' + id); if (!t) return;
    var det = t.closest('details'); if (det) det.open = true;
  });
  function openHashTarget() {
    var id = location.hash.slice(1);
    if (!id || id.indexOf('=') !== -1) return;
    var t = root.querySelector('#' + id);
    if (t) { var det = t.closest('details'); if (det) det.open = true; }
  }
  window.addEventListener('hashchange', openHashTarget);
  openHashTarget();

  window.addEventListener('resize', function () {
    root.classList.toggle('aav-pinpad', window.matchMedia('(max-width:760px)').matches && el('aav-pinbar').classList.contains('on'));
  });

  wireButtons();
  render();
  var integrityResult = integrityCheck();
  root.AAV_INTEGRITY = integrityResult;
  root.AAVCALC = AAVCALC;
  root.AAV_STATE = function () { return state; };
  }
}

if (!customElements.get('s-constipation-calculator')) {
  customElements.define('s-constipation-calculator', ConstipationCalculator);
}
